import dayjs from 'dayjs';

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import DateFilter from 'components/field/DateFilter';
import Card from 'components/global/Card';
import ProgressBar from 'components/global/ProgressBar';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';
import useLazyFetch from 'hooks/useLazyFetch';

import PATHS, { mergePaths } from 'utils/paths';

import ChartHeader from './ChartHeader';
import * as S from './styled/DistributionList.styled';

/**
 * @author @omerrkosar
 * @description This component is used to show distribution list
 * @param {Object} props
 * @param {object} props.data
 * @param {string} [props.title] - Title of the component
 * @param {"with-logo" | "without-logo"} [props.variant] - Variant of the component
 */
const DistributionList = (props) => {
	// paths
	const DELIVERY_COMPANY = mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.CONNECTED_PARTNERS);
	const SALES_CHANNELS = mergePaths(PATHS.SALES_CHANNELS, PATHS.CONNECTED_STORES);

	const [filter, setFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const { fetchURL, title, variant, filtered, defaultDay } = props;

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const rowLimit = 5;
	const navigate = useNavigate();
	const isAuthorized = useAccessControl();

	const ref = useRef();
	const { data, loading } = useLazyFetch({ url: fetchURL, body, triggerElement: ref });

	const Loading = () =>
		Array(rowLimit)
			.fill(0)
			.map((_, index) => (
				<S.ChartContainer key={index}>
					{variant === 'with-logo' && (
						<S.LogoSkeleton>
							<Skeleton width="5rem" height="5rem" />
						</S.LogoSkeleton>
					)}
					<Card padding="0" fullWidth border={false}>
						<S.TopContainer>
							<div className="mb-4">
								<Skeleton width="5rem" height="2rem" />
							</div>
							<div>
								<Skeleton width="5rem" height="2rem" />
							</div>
						</S.TopContainer>
						<S.ChartBackground>
							<Skeleton width="100%" height=".5rem" borderRadius="8px" />
						</S.ChartBackground>
					</Card>
				</S.ChartContainer>
			));
	const NoData = () => (
		<Card padding="0" fullWidth border={false}>
			<S.ChartContainer>
				<Card fullWidth padding="0" border={false}>
					<S.TopContainer isClickable={false}>
						<Typography>noRecordsFoundTitle</Typography>
					</S.TopContainer>
				</Card>
			</S.ChartContainer>
		</Card>
	);

	const clickHandler = (item) => {
		let url;
		if (item.DeliveryCompany) {
			url = DELIVERY_COMPANY;
		} else if (item.scName) {
			url = SALES_CHANNELS;
		}
		if (item.storeName === 'Manual') {
			return;
		}
		url && isAuthorized(url) && navigate(url);
		url && !isAuthorized(url) && navigate(PATHS.OTO_MODULES);
	};

	return (
		<Card ref={ref}>
			<ChartHeader
				title={title}
				filter={filtered && <DateFilter defaultDay={defaultDay} setValue={setFilter} />}
				showDivider={false}
			/>
			<S.Hr />
			{loading && <Loading />}
			{!loading && data?.length === 0 && <NoData />}
			{!loading &&
				data?.length > 0 &&
				data?.map((item, index) => {
					if (index < rowLimit) {
						return (
							<S.ChartContainer key={index}>
								{item?.logo && (
									<>
										<S.LogoContainer>
											<S.Logo
												url={item.logo}
												width="4.5rem"
												height="4.5rem"
												onClick={() => clickHandler(item)}
											/>
										</S.LogoContainer>
										<div className="mx-2" />
									</>
								)}
								{item?.percentage && (
									<S.ProgressBarContainer>
										<S.TopContainer isClickable={item.storeName !== 'Manual'}>
											<Typography
												fontWeight="500"
												className="mb-4"
												onClick={() => clickHandler(item)}>
												{item.city ??
													item.cityName ??
													item.storeName ??
													item.scName ??
													item.DeliveryCompany ??
													item.label}
											</Typography>
											<Typography fontWeight="800">
												{Math.round(item.percentage * 10) / 10}%
											</Typography>
										</S.TopContainer>
										<ProgressBar value={item.percentage} thickness="medium" />
									</S.ProgressBarContainer>
								)}
							</S.ChartContainer>
						);
					}
					return null;
				})}
		</Card>
	);
};
export default DistributionList;
